/**
 * Onboarding form logic, kept out of the component so it can be tested without React.
 *
 * Note what is NOT here: any copy of the upload rules. The server's messages are already written
 * for the applicant ("That file is larger than 15MB…"), and S3 enforces the limits itself, so
 * mirroring the allowlist here would only create something to drift. The presign call sends
 * metadata rather than the file, so the round trip that produces the error is cheap.
 */

/** What the applicant fills in. Strings throughout, because that is what inputs produce. */
export interface OnboardingForm {
  restaurantName: string;
  timezone: string;
  existingPhoneE164: string;
  preferredAreaCode: string;
}

export const EMPTY_ONBOARDING_FORM: OnboardingForm = {
  restaurantName: "",
  timezone: "",
  existingPhoneE164: "",
  preferredAreaCode: ""
};

/**
 * The application as the server reports it. Mirrors ApplicationStatusView.
 *
 * Redeclared rather than imported: it belongs to the onboarding service, and the console already
 * treats each service's HTTP contract as its own thing rather than reaching into its source.
 */
export interface ApplicationView {
  applicationId: string;
  state: string;
  restaurantName?: string;
  contactEmail: string;
  timezone?: string;
  existingPhoneE164?: string;
  preferredAreaCode?: string;
  allocatedPhoneE164?: string;
  nextStep?: string;
  missing?: string[];
  problem?: string;
}

export interface UploadedFileView {
  fileId: string;
  filename: string;
  sizeBytes: number;
  uploadedAtIso: string;
}

/**
 * A short curated list rather than the full IANA set.
 *
 * Every restaurant we can currently serve is in the US, and a 400-entry picker is worse than a
 * short one for the person filling this in. The value is a real IANA zone because hours are stored
 * as local wall-clock time in that zone.
 */
export const ONBOARDING_TIMEZONES: readonly { value: string; label: string }[] = [
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Mountain, no DST (Phoenix)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" }
];

/** Prefill the form from a resumed application. */
export function formFromView(view: ApplicationView | undefined): OnboardingForm {
  if (!view) return { ...EMPTY_ONBOARDING_FORM };
  return {
    restaurantName: view.restaurantName ?? "",
    timezone: view.timezone ?? "",
    existingPhoneE164: view.existingPhoneE164 ?? "",
    preferredAreaCode: view.preferredAreaCode ?? ""
  };
}

/**
 * Turn the form into a patch.
 *
 * Blank strings are OMITTED rather than sent as "". The server treats an absent field as "leave it
 * alone", and its schema rejects an empty string for a field that has a minimum length — so
 * sending "" for an untouched optional field would fail the whole save.
 */
export function patchFromForm(form: OnboardingForm): Record<string, string> {
  const patch: Record<string, string> = {};
  for (const [key, value] of Object.entries(form)) {
    const trimmed = value.trim();
    if (trimmed.length > 0) patch[key] = trimmed;
  }
  return patch;
}

/** Which step of the flow to render. */
export type OnboardingStep = "form" | "submitted";

/**
 * DRAFT is the only editable state, matching the server. Everything from APPLIED onward is a human
 * acting on the application, so the applicant sees status rather than a form.
 */
export function onboardingStep(view: ApplicationView | undefined): OnboardingStep {
  if (!view) return "form";
  return view.state === "DRAFT" ? "form" : "submitted";
}

/** Field-level errors, so a missing timezone shows against the timezone input. */
export interface FieldErrors {
  restaurantName?: string;
  timezone?: string;
}

/**
 * Map the server's `missing` list onto fields.
 *
 * Matched loosely on the field name appearing in the message, because the server owns the wording
 * and should be free to improve it without breaking this mapping. Anything unrecognised is returned
 * so it can still be shown rather than silently dropped.
 */
export function fieldErrorsFrom(missing: readonly string[] = []): {
  fields: FieldErrors;
  other: string[];
} {
  const fields: FieldErrors = {};
  const other: string[] = [];
  for (const problem of missing) {
    const lower = problem.toLowerCase();
    if (lower.includes("restaurant name")) fields.restaurantName = problem;
    else if (lower.includes("timezone")) fields.timezone = problem;
    else other.push(problem);
  }
  return { fields, other };
}

/** Whether submit should be enabled — cheap client-side echo of `submittableProblems`. */
export function canSubmit(form: OnboardingForm): boolean {
  return form.restaurantName.trim().length > 0 && form.timezone.trim().length > 0;
}

/** Human file size for the attachment list. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
